import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Player } from '../../types';
import { useAppContext, View } from '../../context/AppContext';
import { useGroupMemberCount } from '../../hooks/useGroupMemberCount';
import { useActiveTournamentsCount } from '../../hooks/useActiveTournamentsCount';
import { useGroupUnseenStatus } from '../../hooks/useGroupUnseenStatus';
import { collection, getDocs, doc, updateDoc, arrayUnion, setDoc, getDoc } from 'firebase/firestore';
import { db as firestore } from '../../firebaseConfig';
import { getRatingStatus } from '../../utils/glicko2Utils';
import { Modal } from '../ui/Modal';
import { createJoinRequest, notifyAdminsOfJoinRequest } from '../../utils/joinRequestHelpers';
import { getUserRole, getRoleLabel } from '../../utils/permissions';

interface UnifiedGroupCardProps {
    group: Group;
    user: any;
    goToPage: (page: View, params?: any) => void;
    // Used in Groups page
    userData?: any;
    unlockedGroups?: string[];
    initiateSelectGroup?: (group: Group) => void;
    onRename?: (groupId: string, name: string) => void;
    onTogglePrivacy?: (groupId: string, mode: any) => void;
    onDelete?: (groupId: string) => void;
    onLeave?: (groupId: string) => void;
    hasPendingRequest?: boolean;
    // Simplified mode for Dashboard
    simplified?: boolean;
}

/**
 * Unified Group Card Component
 * Used both in Dashboard ("Mes Groupes") and Groups page
 * - Filters ghost players (deleted accounts)
 * - Uses GLOBAL rating from /users collection
 * - Only counts CONFIRMED players for ranking
 */
export const UnifiedGroupCard: React.FC<UnifiedGroupCardProps> = ({
    group,
    user,
    goToPage,
    userData,
    unlockedGroups = [],
    // initiateSelectGroup is in interface but not used - this component handles its own click logic
    onRename,
    onTogglePrivacy,
    onDelete,
    onLeave,
    hasPendingRequest,
    simplified = false
}) => {
    const { t } = useTranslation();
    const { showToast, db, userProfile } = useAppContext();

    // Modal state for NC explanation
    const [showNcModal, setShowNcModal] = useState(false);

    // Join Request State
    const [isRequesting, setIsRequesting] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    const effectivePending = hasPendingRequest || requestSent;

    // Rank calculation state
    const [myRank, setMyRank] = useState<number | null>(null);

    const [isLoadingRank, setIsLoadingRank] = useState(false);
    const [showJoinConfirm, setShowJoinConfirm] = useState(false);
    const [showRequestConfirm, setShowRequestConfirm] = useState(false);
    const [showInviteOnly, setShowInviteOnly] = useState(false);
    const [isJoining, setIsJoining] = useState(false);

    // Real-time member count
    const { ids: playerIds } = useGroupMemberCount(group.id, group.members?.length || 0);

    // Active tournaments count
    const activeCount = useActiveTournamentsCount(group.id);

    // Unseen status (new tournaments or matchmakings)
    const hasUnseen = useGroupUnseenStatus(group, userProfile);

    // Determine membership - rely purely on members array as single source of truth 
    // to prevent ghost group bugs where left groups still appear 'joined' via legacy arrays.
    const isMember = (group.members || []).includes(user?.uid || '');

    const isUnlocked = unlockedGroups.includes(group.id);
    const isOfficial = group.id === 'official_padel_brothers';

    // Handle Card Click (Navigation or Join Request)
    const handleCardClick = async (e: React.MouseEvent) => {
        const isAdmin = (group.players?.find(p => p.id === user?.uid) as any)?.role === 'admin' || group.ownerId === user?.uid;
        if (!simplified && (onRename || onTogglePrivacy || onDelete || onLeave) && (isAdmin || isMember)) {
            // let admin actions handle propagation stop. 
            // if we are here, it means we clicked the BODY of the card.
        }

        // Check if user has an actual player document in this group (not just marked as member)
        const hasPlayerDocument = playerIds.includes(user?.uid || '');

        // If member AND has player document -> Navigate directly
        if ((isMember || simplified || isUnlocked) && hasPlayerDocument) {
            goToPage(View.HOME, { groupId: group.id });
            return;
        }

        // If marked as member but NO player document -> Show join modal to create profile
        // This handles cases like imported groups where user was added to members but not players
        if (isMember && !hasPlayerDocument && user?.uid) {
            setShowJoinConfirm(true);
            return;
        }

        // Handle Privacy Modes for non-members
        if (group.privacyMode === 'locked' || group.privacyMode === 'invite') {
            // Show modal explaining the group is closed
            e.stopPropagation();
            setShowInviteOnly(true);
            return;
        }

        if (group.privacyMode === 'request') {
            if (effectivePending) return; // Already requested
            // Show confirmation modal before sending request
            e.stopPropagation();
            setShowRequestConfirm(true);
            return;
        }

        // Default (Public/Open Group)
        setShowJoinConfirm(true);
    };

    const handleJoinPublicGroup = async () => {
        if (!user) return;
        setIsJoining(true);
        try {
            // Join Group Logic
            // 1. Add to members array in group doc
            const groupRef = doc(firestore, 'groups', group.id);
            await updateDoc(groupRef, {
                members: arrayUnion(user.uid)
            });

            // 2. Add as Player in subcollection with ALL required fields from user profile
            const playerRef = doc(firestore, 'groups', group.id, 'players', user.uid);
            await setDoc(playerRef, {
                id: user.uid,
                name: userData?.displayName || userData?.pseudo || user.displayName || t('common.unknownPlayer'),
                pseudo: userData?.pseudo || user.displayName || t('common.unknownPlayer'),
                displayName: userData?.displayName || user.displayName || t('common.unknownPlayer'),
                email: user.email || '',
                photoURL: userData?.photoURL || user.photoURL || '',
                avatar: userData?.photoURL || user.photoURL || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default',
                role: 'player',
                groupRole: 'player',
                level: userData?.level || t('common.levels.1'),
                hand: userData?.hand || t('profile.handRight'),
                rating: userData?.rating || 1500,
                rd: userData?.rd || 350,
                volatility: userData?.volatility || 0.06,
                matchesPlayed: 0,
                wins: 0,
                losses: 0,
                winRate: 0,
                badges: [],
                ratingHistory: [],
                groupJoinDate: new Date().toISOString(),
                joinedAt: new Date().toISOString()
            });

            // 2. Add as Player in subcollection (if not exists)
            // This is usually handled by a trigger, but for client-side speed/robustness we can attempt it if rules allow
            // Or rely on the user being directed to "Create Profile" inside the group if checking is strict.
            // But per rules, user can create their own player doc.

            // Actually, we'll navigate first, but we need to ensure app context updates.
            // For now, simple member add is enough for "Membership".

            showToast(t('groups.joined', { name: group.name }), 'success');
            goToPage(View.HOME, { groupId: group.id });
        } catch (error) {
            console.error("Error joining group:", error);
            showToast(t('groups.joinError'), "error");
        } finally {
            setIsJoining(false);
            setShowJoinConfirm(false);
        }
    };

    // Handle sending join request after confirmation modal
    const handleSendJoinRequest = async () => {
        if (!user) return;
        setIsRequesting(true);
        try {
            const req = await createJoinRequest(
                user.uid,
                user.displayName || t('common.unknownPlayer'),
                user.photoURL,
                group.id,
                group.name
            );

            if (group.admins || group.ownerId) {
                await notifyAdminsOfJoinRequest(req, group.admins || [], group.ownerId);
            }

            setRequestSent(true);
            showToast(t('groups.requestSentToast'), 'success');
        } catch (err) {
            console.error("Error creating join request:", err);
            showToast(t('groups.requestError'), 'error');
        } finally {
            setIsRequesting(false);
            setShowRequestConfirm(false);
        }
    };



    // Role state
    const [resolvedRole, setResolvedRole] = useState<string | null>(null);

    // FAST: Fetch only the user's role from their player doc (single read)
    useEffect(() => {
        let isMounted = true;
        const fetchUserRole = async () => {
            if (!user?.uid || !group.id) return;

            // NEW: Global Owner Override
            // Check global role first (using userData for appRole)
            const globalRole = getUserRole(userData || user);
            if (globalRole === 'owner') {
                if (isMounted) setResolvedRole('owner');
                return;
            }

            // Check if owner first
            if (group.ownerId === user.uid) {
                if (isMounted) setResolvedRole('owner');
                return;
            }

            try {
                const playerDocRef = doc(firestore, 'groups', group.id, 'players', user.uid);
                const playerSnap = await getDoc(playerDocRef);

                if (!isMounted) return;

                if (playerSnap.exists()) {
                    const playerData = playerSnap.data();
                    if (playerData?.groupRole) {
                        setResolvedRole(playerData.groupRole);
                    } else {
                        // Fallback to local utility if no groupRole field
                        setResolvedRole(getUserRole(user, group));
                    }
                } else {
                    // Player doc doesn't exist, use fallback
                    setResolvedRole(getUserRole(user, group));
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
                // Fallback on error
                setResolvedRole(getUserRole(user, group));
            }
        };

        fetchUserRole();
        return () => { isMounted = false; };
    }, [group.id, user?.uid, group.ownerId, userData]);

    // Use a combined effect for ranking AND role to minimize reads
    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (!isMember || !user?.uid || !group.id) {
                setMyRank(null);
                return;
            }

            try {
                setIsLoadingRank(true);
                const playersRef = collection(firestore, 'groups', group.id, 'players');
                const snapshot = await getDocs(playersRef);

                if (!isMounted) return;

                const players: Player[] = [];
                snapshot.forEach(doc => {
                    players.push({ id: doc.id, ...doc.data() } as Player);
                });

                // EXTRACT ROLE HERE
                const globalRole = getUserRole(userData || user);
                const myPlayerDoc = players.find(p => p.id === user.uid);

                if (globalRole === 'owner') {
                    setResolvedRole('owner');
                } else if (myPlayerDoc?.groupRole) {
                    setResolvedRole(myPlayerDoc.groupRole);
                } else if (group.ownerId === user.uid) {
                    setResolvedRole('owner');
                } else {
                    // Fallback using utils
                    setResolvedRole(getUserRole(user, group));
                }

                // ... existing ranking logic ...
                // Filter ghost players using global users list
                const usersArray = db?.users || [];
                const validUserIds = usersArray.length > 0
                    ? new Set(usersArray.map((u: any) => u.id))
                    : null;

                const validPlayers = validUserIds
                    ? players.filter(p => validUserIds.has(p.id))
                    : players;

                // Enrich with GLOBAL rating and filter CONFIRMED only
                const enrichedPlayers = validPlayers.map(p => {
                    const globalUser = usersArray.find((u: any) => u.id === p.id);
                    const rating = globalUser?.rating ?? p.rating ?? 1500;
                    const rd = globalUser?.rd ?? p.rd ?? 350;
                    const matchesPlayed = globalUser?.matchesPlayed ?? p.matchesPlayed ?? 0;
                    const status = getRatingStatus(rd, matchesPlayed);

                    return {
                        ...p,
                        rating,
                        rd,
                        matchesPlayed,
                        ratingStatus: status
                    };
                });

                // ONLY COUNT CONFIRMED PLAYERS FOR RANKING
                const confirmedPlayers = enrichedPlayers.filter(p => p.ratingStatus === 'confirmed');

                // Check if current user is confirmed
                const currentUserData = enrichedPlayers.find(p => p.id === user.uid);
                const isCurrentUserConfirmed = currentUserData?.ratingStatus === 'confirmed';

                if (!isCurrentUserConfirmed) {
                    // User is not confirmed - don't show rank
                    setMyRank(null);
                } else {
                    // Sort confirmed players by GLOBAL rating descending
                    const sortedConfirmed = confirmedPlayers.sort((a, b) => (b.rating || 1500) - (a.rating || 1500));
                    const index = sortedConfirmed.findIndex(p => p.id === user.uid);

                    if (index !== -1) {
                        setMyRank(index + 1);
                    } else {
                        setMyRank(null);
                    }
                }
            } catch (error) {
                console.error("Error fetching rank/role:", error);
            } finally {
                if (isMounted) setIsLoadingRank(false);
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [group.id, user?.uid, isMember, db?.users, userData]);

    // Calculate valid member count (excluding ghost players)
    // Ghost players are those in the /players subcollection but NOT in /users collection
    // This happens with imported groups where players haven't been merged to real accounts

    // Calculate valid member count (strictly excluding ghost players / bots / deleted users)
    const displayCount = React.useMemo(() => {
        // If db.users is loaded, filter player IDs from subcollection to only count real registered users
        if (db?.users && db.users.length > 0) {
            const validUserIds = new Set(db.users.map((u: any) => u.id));
            if (playerIds && playerIds.length > 0) {
                return playerIds.filter(pid => validUserIds.has(pid)).length;
            }
            if (group.members && group.members.length > 0) {
                return group.members.filter((mid: string) => validUserIds.has(mid)).length;
            }
        }
        // Fallback: use group.members array (which only holds real member user IDs added upon join)
        return group.members?.length || 0;
    }, [playerIds, db?.users, group.members]);

    // Special handling for Official Group Logo
    const logoUrl = isOfficial ? '/official_logo.webp' : (group.logoUrl || null);



    // Image error state
    const [imgError, setImgError] = useState(false);

    return (
        <div className="relative">
            <div
                onClick={handleCardClick}
                className={`
                    relative 
                    bg-surface-2/50 backdrop-blur-sm 
                    border border-glass-border 
                    rounded-2xl p-3 
                    cursor-pointer 
                    transition-all duration-300 
                    hover:bg-surface-2 
                    hover:border-primary/30 
                    group
                    ${isMember ? 'hover:shadow-[0_0_20px_rgba(139,92,246,0.15)]' : ''}
                `}
            >
                {/* ABSOLUTE STATUS BADGE (Top Right) */}
                {hasUnseen && (
                    <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-br from-red-500 to-rose-600 w-4 h-4 rounded-full border-2 border-surface-1 shadow-md z-10 flex items-center justify-center">
                        <span className="text-white text-[10px] font-black">!</span>
                    </span>
                )}

                <div className="flex items-center gap-4">
                    {/* Group Logo */}
                    {logoUrl && !imgError && (logoUrl.startsWith('http') || logoUrl.startsWith('/') || logoUrl.startsWith('blob:')) ? (
                        <div className={`relative ${isOfficial ? 'p-[2px] rounded-xl bg-gradient-to-br from-yellow-300 to-yellow-500' : ''} flex-shrink-0 group-hover:scale-105 transition-transform duration-300 shadow-lg`}>
                            <img
                                src={logoUrl}
                                alt={group.name}
                                onError={() => setImgError(true)}
                                loading="lazy"
                                className={`w-12 h-12 rounded-lg object-cover shadow-md ${isOfficial ? 'border-2 border-black' : ''}`}
                                width="48"
                                height="48"
                            />
                        </div>
                    ) : (
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-105 transition-transform duration-300 ${isOfficial ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-primary to-purple-600'}`}>
                            {logoUrl && !imgError ? (
                                <span className="text-2xl" role="img" aria-label="Group Logo">
                                    {logoUrl}
                                </span>
                            ) : (
                                <span className="material-symbols-outlined text-white text-xl">
                                    {isOfficial ? 'verified' : 'groups'}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Group Info */}
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                        <div className="flex items-center gap-2">
                            <h3 className={`font-bold text-text-primary flex items-center gap-1 whitespace-nowrap min-w-0 flex-1 overflow-hidden ${group.name.length > 25 ? 'text-[9px] sm:text-[10px]' :
                                group.name.length > 18 ? 'text-[10px] sm:text-xs' :
                                    group.name.length > 14 ? 'text-xs sm:text-sm' :
                                        'text-sm sm:text-base'
                                }`}>
                                <span>{group.name}</span>
                                {isOfficial && (
                                    <span className="material-symbols-outlined text-yellow-400 text-sm fill-current flex-shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>
                                        verified
                                    </span>
                                )}
                            </h3>

                            {/* Status Badges - Right Aligned in Header */}
                            {/* Moved from absolute position to inline header */}
                            <div className="ml-auto mr-4 flex items-center pointer-events-none">
                                {/* Membre */}
                                {isMember && (
                                    <>
                                        {(() => {
                                            // Use resolvedRole (fetched from Firestore) with fallback to getUserRole
                                            const role = resolvedRole || getUserRole(user, group);
                                            // Special badges for specific roles
                                            if (role !== 'player' && role !== 'user') {
                                                const label = t(`groups.roleLabels.${role}`, { defaultValue: role });

                                                const colorClass = role === 'admin' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' :
                                                    role === 'co_admin' ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20' :
                                                        role === 'organizer' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                                                            role === 'referee' ? 'text-orange-400 bg-orange-500/10 border-orange-500/20' :
                                                                'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'; // Owner

                                                const icon = role === 'admin' ? 'shield' :
                                                    role === 'co_admin' ? 'security' :
                                                        role === 'organizer' ? 'emoji_events' :
                                                            role === 'referee' ? 'sports' : 'workspace_premium';

                                                return (
                                                    <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border shadow-sm backdrop-blur-md whitespace-nowrap ${colorClass}`} title={getRoleLabel(role as any)}>
                                                        <span className="material-symbols-outlined text-[14px] font-bold icon-filled" style={{ fontSize: '14px' }}>
                                                            {icon}
                                                        </span>
                                                        <span className="font-bold uppercase">
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            }

                                            // Default Member Badge
                                            return (
                                                <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 shadow-sm backdrop-blur-md whitespace-nowrap">
                                                    <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                    <span className="font-bold">{t('roles.player')}</span>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                                {/* Pending */}
                                {!isMember && effectivePending && (
                                    <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 shadow-sm backdrop-blur-md whitespace-nowrap">
                                        <span className="material-symbols-outlined text-[14px]">schedule</span>
                                        <span>{t('groups.request.sending')}</span>
                                    </div>
                                )}
                                {/* Privacy Modes */}
                                {!isMember && !simplified && !effectivePending && (
                                    <>
                                        {/* Public (Open) */}
                                        {(!group.privacyMode || group.privacyMode === 'open') && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-green-500/10 text-green-400 border border-green-500/20 shadow-sm backdrop-blur-md whitespace-nowrap">
                                                <span className="material-symbols-outlined text-[14px]">lock_open</span>
                                                <span className="font-bold">{t('groups.filterPublic')}</span>
                                            </div>
                                        )}
                                        {group.privacyMode === 'locked' && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-error/10 text-error border border-error/20 shadow-sm backdrop-blur-md whitespace-nowrap">
                                                <span className="material-symbols-outlined text-[14px]">lock</span>
                                                <span className="hidden sm:inline">{t('groups.filterPrivate')}</span>
                                            </div>
                                        )}
                                        {group.privacyMode === 'invite' && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-error/10 text-error border border-error/20 shadow-sm backdrop-blur-md whitespace-nowrap">
                                                <span className="material-symbols-outlined text-[14px]">mail</span>
                                                <span>{t('invite.title')}</span>
                                            </div>
                                        )}
                                        {group.privacyMode === 'request' && (
                                            <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-error/10 text-error border border-error/20 shadow-sm backdrop-blur-md animate-pulse whitespace-nowrap">
                                                <span className="material-symbols-outlined text-[14px]">lock</span>
                                                <span>{t('groups.request.status')}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3 text-xs text-text-secondary mt-2 w-full">
                            {/* Members (Icon + Count) */}
                            <div className="flex items-center gap-1" title={t('groups.players')}>
                                <span className="material-symbols-outlined text-[16px]">person</span>
                                <span className="font-bold text-text-primary">{displayCount}</span>
                            </div>

                            {/* Tournaments */}
                            {(activeCount || 0) > 0 && (
                                <div className="flex items-center gap-0.5 text-primary px-1.5 py-0.5 rounded-full" title={t('groups.activeTournaments')}>
                                    <span className="material-symbols-outlined text-text-primary text-[14px]">emoji_events</span>
                                    <span className="font-bold">{activeCount}</span>
                                </div>
                            )}

                            {/* Group ID (Shortened for imported groups) */}
                            <span className="text-[10px] text-text-tertiary whitespace-nowrap font-mono uppercase">
                                ID: {group.code || (group.id?.startsWith('imported_') ? `IMP-${group.id.slice(-4).toUpperCase()}` : group.id) || '---'}
                            </span>

                            {/* Right Side: Rank + Chevron */}
                            <div className="ml-auto flex items-center gap-2">
                                {isMember && (
                                    <div className="text-right">
                                        {isLoadingRank ? (
                                            <span className="material-symbols-outlined animate-spin text-text-secondary text-sm">
                                                autorenew
                                            </span>
                                        ) : myRank ? (
                                            <div className="font-bold text-primary text-base">
                                                #{myRank}
                                            </div>
                                        ) : (
                                            <div
                                                className="text-xs text-text-secondary italic cursor-help hover:text-primary transition-colors"
                                                title={t('groups.nc.desc')}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowNcModal(true);
                                                }}
                                            >
                                                {t('groups.nc.title')}
                                            </div>
                                        )}
                                    </div>
                                )}
                                <span className="material-symbols-outlined text-text-secondary group-hover:text-primary transition-colors">
                                    chevron_right
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions - Prevent propagation to card click */}
            {!simplified && (onRename || onTogglePrivacy || onDelete || onLeave) && (
                ((group.players?.find(p => p.id === user?.uid) as any)?.role === 'admin' || group.ownerId === user?.uid || isMember) && (
                    <div className="absolute top-2 right-12 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-surface-1/80 backdrop-blur-sm rounded-lg p-1 shadow-sm">
                        {onRename && ((group.players?.find(p => p.id === user?.uid) as any)?.role === 'admin' || group.ownerId === user?.uid) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const newName = prompt(t('groups.renamePrompt'), group.name);
                                    if (newName && newName !== group.name) onRename(group.id, newName);
                                }}
                                className="p-1.5 hover:bg-surface-3 rounded-md text-text-secondary hover:text-primary transition-colors"
                                title={t('common.rename', 'Renommer')}
                            >
                                <span className="material-symbols-outlined text-sm">edit</span>
                            </button>
                        )}
                        {onTogglePrivacy && ((group.players?.find(p => p.id === user?.uid) as any)?.role === 'admin' || group.ownerId === user?.uid) && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTogglePrivacy(group.id, group.privacyMode);
                                }}
                                className="p-1.5 hover:bg-surface-3 rounded-md text-text-secondary hover:text-primary transition-colors"
                                title={group.privacyMode === 'locked' ? t('groups.makePublic') : t('groups.makePrivate')}
                            >
                                <span className="material-symbols-outlined text-sm">
                                    {group.privacyMode === 'locked' ? 'lock_open' : 'lock'}
                                </span>
                            </button>
                        )}
                        {onDelete && group.ownerId === user?.uid && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(group.id);
                                }}
                                className="p-1.5 hover:bg-surface-3 rounded-md text-text-secondary hover:text-error transition-colors"
                                title={t('common.delete')}
                            >
                                <span className="material-symbols-outlined text-sm">delete</span>
                            </button>
                        )}
                        {onLeave && isMember && group.ownerId !== user?.uid && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onLeave(group.id);
                                }}
                                className="p-1.5 hover:bg-surface-3 rounded-md text-text-secondary hover:text-error transition-colors"
                                title={t('groups.leaveBtn', 'Quitter le groupe')}
                            >
                                <span className="material-symbols-outlined text-sm">logout</span>
                            </button>
                        )}
                    </div>
                )
            )}

            {showNcModal && (
                <Modal
                    isOpen={showNcModal}
                    onClose={() => setShowNcModal(false)}
                    title={t('groups.nc.title')}
                >
                    <div className="space-y-4">
                        <p className="text-text-secondary">
                            {t('groups.nc.desc')}
                        </p>
                        <ul className="list-disc list-inside text-sm text-text-secondary space-y-1">
                            <li>{t('groups.nc.rules.minMatches')}</li>
                            <li>{t('groups.nc.rules.validatedOnly')}</li>
                        </ul>
                        <div className="flex justify-end">
                            <button
                                onClick={() => setShowNcModal(false)}
                                className="px-4 py-2 rounded-lg bg-surface-3 hover:bg-surface-4 text-text-primary transition-colors"
                            >
                                {t('groups.nc.understood')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Join Confirmation Modal */}
            {showJoinConfirm && (
                <Modal
                    isOpen={showJoinConfirm}
                    onClose={() => setShowJoinConfirm(false)}
                    title={t('groups.join.prompt')}
                >
                    <div className="space-y-6">
                        <div className="flex flex-col items-center gap-4 text-center">
                            {/* Group Logo/Icon */}
                            <div className="w-20 h-20 rounded-2xl bg-surface-2 flex items-center justify-center border border-white/5 shadow-inner">
                                {logoUrl && !imgError && (logoUrl.startsWith('http') || logoUrl.startsWith('/') || logoUrl.startsWith('blob:')) ? (
                                    <img
                                        src={logoUrl}
                                        alt={group.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover rounded-2xl"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <span className="material-symbols-outlined text-4xl text-primary">groups</span>
                                )}
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-text-primary">{group.name}</h3>
                                <p className="text-sm text-text-secondary mt-1">
                                    {t('groups.join.desc')}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end pt-4 border-t border-white/5">
                            <button
                                onClick={() => setShowJoinConfirm(false)}
                                className="px-4 py-2 rounded-xl bg-surface-2 hover:bg-surface-3 text-text-primary transition-colors font-medium"
                                disabled={isJoining}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleJoinPublicGroup}
                                className="px-6 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 flex items-center gap-2"
                                disabled={isJoining}
                            >
                                {isJoining ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin text-lg">autorenew</span>
                                        <span>{t('groups.join.joining')}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined">login</span>
                                        <span>{t('groups.join.btn')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Request Confirmation Modal - For groups requiring validation */}
            {showRequestConfirm && (
                <Modal
                    isOpen={showRequestConfirm}
                    onClose={() => setShowRequestConfirm(false)}
                    title={t('groups.request.btn')}
                >
                    <div className="space-y-6">
                        <div className="flex flex-col items-center gap-4 text-center">
                            {/* Group Logo/Icon */}
                            <div className="w-20 h-20 rounded-2xl bg-surface-2 flex items-center justify-center border border-white/5 shadow-inner">
                                {logoUrl && !imgError && (logoUrl.startsWith('http') || logoUrl.startsWith('/') || logoUrl.startsWith('blob:')) ? (
                                    <img
                                        src={logoUrl}
                                        alt={group.name}
                                        className="w-full h-full object-cover rounded-2xl"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <span className="material-symbols-outlined text-4xl text-primary">groups</span>
                                )}
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-text-primary">{group.name}</h3>
                                <p className="text-sm text-text-secondary mt-1">
                                    {t('groups.request.desc')}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end pt-4 border-t border-white/5">
                            <button
                                onClick={() => setShowRequestConfirm(false)}
                                className="px-4 py-2 rounded-xl bg-surface-2 hover:bg-surface-3 text-text-primary transition-colors font-medium"
                                disabled={isRequesting}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSendJoinRequest}
                                className="px-6 py-2 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold shadow-lg shadow-primary/20 flex items-center gap-2"
                                disabled={isRequesting}
                            >
                                {isRequesting ? (
                                    <>
                                        <span className="material-symbols-outlined animate-spin text-lg">autorenew</span>
                                        <span>{t('groups.request.sending')}</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-symbols-outlined">send</span>
                                        <span>{t('groups.request.btn')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Invite Only Group Modal */}
            {showInviteOnly && (
                <Modal
                    isOpen={showInviteOnly}
                    onClose={() => setShowInviteOnly(false)}
                    title={t('groups.join.inviteOnly')}
                >
                    <div className="space-y-6">
                        <div className="flex flex-col items-center gap-4 text-center">
                            {/* Group Logo/Icon */}
                            <div className="w-20 h-20 rounded-2xl bg-surface-2 flex items-center justify-center border border-white/5 shadow-inner">
                                {logoUrl && !imgError && (logoUrl.startsWith('http') || logoUrl.startsWith('/') || logoUrl.startsWith('blob:')) ? (
                                    <img
                                        src={logoUrl}
                                        alt={group.name}
                                        className="w-full h-full object-cover rounded-2xl"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <span className="material-symbols-outlined text-4xl text-primary">groups</span>
                                )}
                            </div>

                            <div>
                                <h3 className="text-xl font-bold text-text-primary">{group.name}</h3>
                                <p className="text-sm text-text-secondary mt-1">
                                    {t('groups.join.inviteOnlyDesc')}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-center pt-4 border-t border-white/5">
                            <button
                                onClick={() => setShowInviteOnly(false)}
                                className="w-full px-4 py-3 rounded-xl bg-surface-2 hover:bg-surface-3 text-text-primary font-bold transition-colors"
                            >
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
